import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import {
  CharacterizationBasis,
  PackingGroup,
  PhysicalState,
  WasteProfileConstituentInput,
  WasteProfileInput,
  WasteStreamDotProfileInput,
} from "generated-graphql/graphql";

import { WasteStreamProfileData } from "./types";

export interface ConstituentFormRow {
  name: string;
  casNumber: string;
  minPercent: string;
  maxPercent: string;
}

/**
 * Numeric profile fields are held as strings so a cleared input is an empty
 * string rather than NaN; `toFloat` is the single place that turns them back
 * into the nullable Floats the mutation takes.
 */
export interface WasteProfileFormValues {
  physicalState: PhysicalState | null;
  phLow: string;
  phHigh: string;
  flashPointF: string;
  specificGravity: string;
  physicalDescription: string;
  isIgnitable: boolean;
  isCorrosive: boolean;
  isReactive: boolean;
  isToxic: boolean;
  generatingProcess: string;
  basis: CharacterizationBasis | null;
  profileNumber: string;
  tsdfName: string;
  tsdfProfileId: string;
  constituents: ConstituentFormRow[];
  dotProperShippingName: string;
  dotIdNumber: string;
  dotHazardClass: string;
  dotPackingGroup: PackingGroup | null;
  dotHazardous: boolean;
}

const toFloat = (value: string): number | null => {
  const trimmed = value?.trim() ?? "";
  if (trimmed === "") return null;
  const parsed = safeParseFloat(trimmed);
  return parsed != null && Number.isFinite(parsed) ? parsed : null;
};

/**
 * An optionally signed decimal, and nothing else. `safeParseFloat` cannot do
 * this job: it wraps `parseFloat`, which takes a numeric prefix, so "12abc"
 * comes back as 12 and "heavy" comes back as null. Each one replaces what the
 * user typed with a value they never entered, in fields that feed compliance
 * paperwork. The only strict check the app already has is the `Number()` in
 * `FormTextField`'s `intOnly`, which is integer-only, so the shape is spelled
 * out here instead.
 *
 * Commas are matched here rather than stripped before the test. `safeParseFloat`
 * strips them, so stripping first would accept "1,2" and store 12, and would
 * reduce a lone "," to blank and clear an existing measurement. Only commas in
 * thousands positions are allowed, which keeps "1,000" working.
 *
 * Checked on submit rather than per keystroke because these values can be
 * negative and fractional: filtering keystrokes would reject the "-" and "."
 * of a half-typed "-40.5".
 */
const NUMERIC_PATTERN = /^[-+]?((\d+|\d{1,3}(,\d{3})+)(\.\d*)?|\.\d+)$/;

export const isBlankOrNumeric = (value: string): boolean => {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" || NUMERIC_PATTERN.test(trimmed);
};

/**
 * Attach to every numeric profile field so `toFloat` only ever sees a blank or
 * a real number. The parameter is `unknown` because react-hook-form types a
 * shared rule against the union of every field's value; these rules only go on
 * string fields, so anything else has nothing to check and passes.
 */
export const NUMERIC_FIELD_RULES = {
  validate: (value: unknown) =>
    typeof value !== "string" || isBlankOrNumeric(value) || "Enter a number",
};

const toNullableString = (value: string): string | null => value?.trim() || null;

const numberToFormString = (value?: number | null): string => (value == null ? "" : `${value}`);

export const toFormValues = (wasteStream: WasteStreamProfileData): WasteProfileFormValues => {
  const profile = wasteStream.profile;
  return {
    physicalState: profile?.physicalState ?? null,
    phLow: numberToFormString(profile?.phLow),
    phHigh: numberToFormString(profile?.phHigh),
    flashPointF: numberToFormString(profile?.flashPointF),
    specificGravity: numberToFormString(profile?.specificGravity),
    physicalDescription: profile?.physicalDescription ?? "",
    isIgnitable: profile?.isIgnitable ?? false,
    isCorrosive: profile?.isCorrosive ?? false,
    isReactive: profile?.isReactive ?? false,
    isToxic: profile?.isToxic ?? false,
    generatingProcess: profile?.generatingProcess ?? "",
    basis: profile?.basis ?? null,
    profileNumber: profile?.profileNumber ?? "",
    tsdfName: profile?.tsdfName ?? "",
    tsdfProfileId: profile?.tsdfProfileId ?? "",
    constituents: (profile?.constituents ?? []).map((constituent) => ({
      name: constituent.name,
      casNumber: constituent.casNumber ?? "",
      minPercent: numberToFormString(constituent.minPercent),
      maxPercent: numberToFormString(constituent.maxPercent),
    })),
    dotProperShippingName: wasteStream.dotProperShippingName ?? "",
    dotIdNumber: wasteStream.dotIdNumber ?? "",
    dotHazardClass: wasteStream.dotHazardClass ?? "",
    dotPackingGroup: wasteStream.dotPackingGroup ?? null,
    dotHazardous: wasteStream.dotHazardous ?? false,
  };
};

/** True only when the user has typed nothing at all into the row. */
export const isBlankConstituentRow = (row: ConstituentFormRow): boolean =>
  !row.name?.trim() && !row.casNumber?.trim() && !row.minPercent?.trim() && !row.maxPercent?.trim();

/**
 * Only a wholly untouched row is dropped — that's the empty row the user
 * added and never filled in, and dropping it keeps it from failing the
 * mutation's non-null `name`. A row with a CAS number or a percentage but no
 * name is real input, so it is kept and the name field's own rule blocks the
 * save until it's named, rather than the row vanishing on a successful save.
 */
const toConstituentInputs = (rows: ConstituentFormRow[]): WasteProfileConstituentInput[] =>
  rows
    .filter((row) => !isBlankConstituentRow(row))
    .map((row) => ({
      name: row.name.trim(),
      casNumber: toNullableString(row.casNumber),
      minPercent: toFloat(row.minPercent),
      maxPercent: toFloat(row.maxPercent),
    }));

export const toProfileInput = (
  wasteStreamId: string,
  values: WasteProfileFormValues
): WasteProfileInput => ({
  wasteStreamId,
  physicalState: values.physicalState,
  phLow: toFloat(values.phLow),
  phHigh: toFloat(values.phHigh),
  flashPointF: toFloat(values.flashPointF),
  specificGravity: toFloat(values.specificGravity),
  physicalDescription: toNullableString(values.physicalDescription),
  isIgnitable: values.isIgnitable,
  isCorrosive: values.isCorrosive,
  isReactive: values.isReactive,
  isToxic: values.isToxic,
  generatingProcess: toNullableString(values.generatingProcess),
  basis: values.basis,
  profileNumber: toNullableString(values.profileNumber),
  tsdfName: toNullableString(values.tsdfName),
  tsdfProfileId: toNullableString(values.tsdfProfileId),
  constituents: toConstituentInputs(values.constituents),
});

export const toDotInput = (
  wasteStreamId: string,
  values: WasteProfileFormValues
): WasteStreamDotProfileInput => ({
  wasteStreamId,
  dotProperShippingName: toNullableString(values.dotProperShippingName),
  dotIdNumber: toNullableString(values.dotIdNumber),
  dotHazardClass: toNullableString(values.dotHazardClass),
  dotPackingGroup: values.dotPackingGroup,
  dotHazardous: values.dotHazardous,
});
